// src/components/dashboard/DashboardStats.js
import React from "react";
import { Box, useTheme } from "@mui/material";
import {
  People,
  Assignment,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Sell,
} from "@mui/icons-material";
import StatCard from "../ui/StatCard";
import { useCollection } from "../../hooks/useCollection";
import { useNavigate } from "react-router-dom";

// Función auxiliar para formatear moneda (igual que en FinancialAnalysis)
const formatCurrency = (value) => {
  const num = Number(value) || 0;
  const parts = num.toFixed(2).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formattedInteger},${decimalPart}`;
};

const DashboardStats = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: clients } = useCollection("clients");
  const { data: projects } = useCollection("projects");
  const { data: salesInvoices } = useCollection("salesInvoices");
  const { data: purchaseInvoices } = useCollection("purchaseInvoices");
  const { data: contacts } = useCollection("contacts");

  // Clientes activos
  const activeClients =
    clients?.filter((c) => c.isActive !== false)?.length || 0;

  // Proyectos activos (no completados)
  const activeProjects =
    projects?.filter((p) => p.status !== "completed" && p.isActive !== false)?.length || 0;

  // SOLO facturas de venta PAGADAS
  const paidSalesInvoices =
    salesInvoices?.filter((i) => i.isActive !== false && i.status === "paid") || [];
  
  const totalSalesInvoices = paidSalesInvoices.length;

  // SOLO facturas de compra PAGADAS
  const paidPurchaseInvoices =
    purchaseInvoices?.filter((i) => i.isActive !== false && i.status === "paid") || [];
  
  const totalPurchaseInvoices = paidPurchaseInvoices.length;

  // Total de ventas SIN IVA (base imponible) - SOLO PAGADAS
  const totalSalesBase = paidSalesInvoices.reduce((sum, i) => sum + (Number(i.baseAmount) || 0), 0);

  // Total de ventas CON IVA - SOLO PAGADAS
  const totalSales = paidSalesInvoices.reduce((sum, i) => sum + (Number(i.total) || 0), 0);

  // Total de compras SIN IVA (base imponible) - SOLO PAGADAS
  const totalPurchasesBase = paidPurchaseInvoices.reduce((sum, i) => sum + (Number(i.baseAmount) || 0), 0);

  // Total de compras CON IVA - SOLO PAGADAS
  const totalPurchases = paidPurchaseInvoices.reduce((sum, i) => sum + (Number(i.total) || 0), 0);

  // Beneficio total = bases imponibles (sin IVA) - SOLO PAGADAS
  const totalProfit = totalSalesBase - totalPurchasesBase;

  // Contactos activos
  const totalContacts =
    contacts?.filter((c) => c.isActive !== false)?.length || 0;

  // 🌊 Paleta EPC (azules + verdes)
  const EPC = {
    blueDeep: "#005f65",
    blue: "#00afa3",
    blueLight: "#00e1d2",
    aqua: "#42fff2",
    mint: "#95ffef",
    greenDeep: "#0e502d",
    green: "#00935d",
    greenLight: "#00c07c",
    greenBright: "#00ffbb",
    greenSoft: "#56ffd2",
  };

  const stats = [
    {
      title: "Clientes Activos",
      value: activeClients,
      icon: <People />,
      color: EPC.blue,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(0, 175, 163, 0.18)"
          : "rgba(0, 95, 101, 0.10)",
      trend: activeClients,
      trendIcon: <TrendingUp fontSize="small" />,
      onClick: () => navigate("/clients"),
    },
    {
      title: "Contactos",
      value: totalContacts,
      icon: <People />,
      color: EPC.blueLight,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(66, 255, 242, 0.15)"
          : "rgba(0, 225, 210, 0.10)",
      trend: totalContacts,
      trendIcon: <TrendingUp fontSize="small" />,
      onClick: () => navigate("/contacts"),
    },
    {
      title: "Proyectos en Curso",
      value: activeProjects,
      icon: <Assignment />,
      color: EPC.green,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(0, 147, 93, 0.18)"
          : "rgba(0, 147, 93, 0.10)",
      trend: activeProjects,
      trendIcon: <TrendingUp fontSize="small" />,
      onClick: () => navigate("/projects"),
    },
    {
      title: "Ventas Cobradas",
      value: `${formatCurrency(totalSales)}€`, // Usando formatCurrency para 2 decimales
      icon: <Sell />,
      color: EPC.blueDeep,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(0, 95, 101, 0.18)"
          : "rgba(0, 95, 101, 0.10)",
      subtitle: `${totalSalesInvoices} facturas pagadas`,
      onClick: () => navigate("/sales"),
    },
    {
      title: "Compras Pagadas",
      value: `${formatCurrency(totalPurchases)}€`, // Usando formatCurrency para 2 decimales
      icon: <ShoppingCart />,
      color: "#1abc9c",
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(245, 124, 0, 0.18)"
          : "rgba(245, 124, 0, 0.10)",
      subtitle: `${totalPurchaseInvoices} facturas pagadas`,
      onClick: () => navigate("/purchases"),
    },
    {
      title: "Beneficio Real",
      value: `${formatCurrency(totalProfit)}€`, // Usando formatCurrency para 2 decimales
      icon: <AttachMoney />,
      color: totalProfit >= 0 ? EPC.greenLight : "#f44336",
      bgColor:
        theme.palette.mode === "dark"
          ? totalProfit >= 0
            ? "rgba(0, 192, 124, 0.18)"
            : "rgba(244, 67, 54, 0.18)"
          : totalProfit >= 0
            ? "rgba(0, 192, 124, 0.10)"
            : "rgba(244, 67, 54, 0.10)",
      trend: totalProfit,
      trendIcon:
        totalProfit >= 0
          ? <TrendingUp fontSize="small" />
          : <TrendingDown fontSize="small" />,
      subtitle: "Solo facturas pagadas (Sin IVA)",
      onClick: () => navigate("/financial-analysis"),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1, sm: 1.5, md: 2 },
        flexWrap: "wrap",
        width: "100%",
        mb: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {stats.map((stat, index) => (
        <Box
          key={index}
          sx={{
            flex: "1 1 200px",
            minWidth: { xs: "140px", sm: "160px", md: "180px" },
            maxWidth: { xs: "calc(50% - 4px)", sm: "none" },
          }}
        >
          <StatCard {...stat} />
        </Box>
      ))}
    </Box>
  );
};

export default DashboardStats;